import { Injectable } from '@angular/core';
import { CorporationUserRole, User,EmailUserDetailModel } from '../models/user.model';
import { BehaviorSubject, defer, Observable,Subject } from 'rxjs';
import DevExpress from 'devextreme/bundles/dx.all';
import { HhsgSharedHttpService } from './http.service';
import { HhsgSharedAspDataStoreService } from './asp-data-store.service';
import { HhsgSharedStuderEmployee } from '../models/sgportal-data-api-models/employee.model';
import { HhsgSharedClient } from '../models/client.model';
import { HhsgSharedClientApplication } from '../models/client-application.model';
import { HhsgSharedCorporation } from '../models/corporation.model';
import { HhsgSharedConfigService } from './config.service';
import { ResponseStatus } from '../models/response-status.model';
import { HttpParams } from '@angular/common/http';
import { HhsgUserGridModel } from '../models/user-grid.model';

export class ClientWithApplications {
  constructor(public client = new HhsgSharedClient(), public clientApplications: HhsgSharedClientApplication[] = []) {}
}

export class ClientToOrganization {
  constructor(public clientId: number, public organizationId: number) {}
}

export class ClientWithIndex {
  constructor(public client: HhsgSharedClient, public index: number) {}
}

@Injectable()
export class HhsgSharedUserAdminService {
  public selectedCorporation: HhsgSharedCorporation;
  public selectedCorporationId: number;
  public selectedUserCorporation: HhsgSharedCorporation;

  public formStatus: string;
  public selectedUser: User;
  public errorMessages: string[] = [];

  public ClientWithApplications = new ClientWithApplications();
  public ClientsWithApplications = new Array<ClientWithApplications>();
  public ClientsToOrganizations = new Array<ClientToOrganization>();
  public UserEmployees = new Array<HhsgSharedStuderEmployee>();
  public UserClients = new Array<ClientWithIndex>();
  public isSubmitted = false;
  public confirmSaving = false;

  private readonly _apiServer: string;
  private readonly _controllerName = 'Users';

  private  activeUserSource = new Subject<User>();
  public activeUserSource$ = this.activeUserSource.asObservable();

  private  selectedCorporationChangeSource = new BehaviorSubject<number>(0);
  public selectedCorporationChangeSource$ = this.selectedCorporationChangeSource.asObservable();

  private  corporationExportSource = new BehaviorSubject<boolean>(false);
  public corporationExportSource$ = this.corporationExportSource.asObservable();

  constructor(
    private _configService: HhsgSharedConfigService,
    private _httpService: HhsgSharedHttpService,
    private _aspDataStoreService: HhsgSharedAspDataStoreService) {
    this._apiServer = _configService.appSettings.stsServer;
  }

  public clientIdToOrganizationId(clientId: number): number {
    var returnOrganizationId = -1;
    if (this.ClientsToOrganizations &&
      this.ClientsToOrganizations.length > 0 &&
      this.ClientsToOrganizations.findIndex(x => x.clientId === clientId) > -1)  {
      returnOrganizationId = this.ClientsToOrganizations.find(x => x.clientId === clientId).organizationId;
    }
    return returnOrganizationId;
  }

  public async createNewUser(newUserRequest: User): Promise<any> {
    return await this._httpService.doPostRequestAsync<any, any>(this._apiServer, this._controllerName, 'CreateUser/false', newUserRequest);
  }

  public async enableUser(userId: number): Promise<any> {
    return await this._httpService.doPostRequestAsync<any, any>(this._apiServer, this._controllerName, `EnableUser/${userId}`, null);
  }

  public async getAsUserModel(userId: number): Promise<User> {
    return this._httpService.doGetRequestAsync<User>(this._apiServer, this._controllerName, `GetAsUserModel/${userId}`);
  }

  public getAsUserModelObservable(userId : number) : Observable<User>{
    let user = this._httpService.doGetRequestObservable<User>(this._apiServer, this._controllerName, `GetAsUserModel/${userId}`);
    return user;
  }

  public async getUserDetailsByEmail(email: string): Promise<EmailUserDetailModel> {
    return this._httpService.doGetRequestAsync<EmailUserDetailModel>(this._apiServer, this._controllerName, `GetUserDetailsByEmail/${email}`);
  }

  public async getEditableUserRoles(corporationId:number): Promise<CorporationUserRole> {
    return this._httpService.doGetRequestAsync<CorporationUserRole>(this._apiServer, this._controllerName, `GetEditableUserRoles/${corporationId}`);
  }

  public getUsersForCorporation(corporationId: number, applicationAbbreviation: string, filterByCurrentApplication: boolean): Observable<DevExpress.data.CustomStore> {
    return defer(() => this._aspDataStoreService.createASPDataStore({
      loadUrl: `${ this._apiServer }/${ this._controllerName }/GetUsersForCorporation/${corporationId}/${applicationAbbreviation}/${filterByCurrentApplication}`
    }));
  }

  public getAllUsersForCorporation(corporationId: number, applicationAbbreviation: string, filterByCurrentApplication: boolean): Observable<HhsgUserGridModel[]> {
    return this._httpService.doGetRequestObservable<HhsgUserGridModel[]>(this._apiServer, this._controllerName, `GetAllUsersForCorporation/${corporationId}/${applicationAbbreviation}/${filterByCurrentApplication}`);
  }

  public OrganizationIdToClientId(organizationId: number): number {
    var returnClientId = -1;
    if (this.ClientsToOrganizations &&
      this.ClientsToOrganizations.length > 0 &&
      this.ClientsToOrganizations.findIndex(x => x.organizationId === organizationId) > -1)  {
      returnClientId = this.ClientsToOrganizations.find(x => x.organizationId === organizationId).clientId;
    }
    return returnClientId;
  }

  public async updateUserProfile(updateUserRequest: User): Promise<any> {
    return await this._httpService.doPostRequestAsync<any, any>(this._apiServer, this._controllerName, 'UpdateUserProfile/false', updateUserRequest);
  }

  public async upsert(updateUserRequest: any): Promise<any> {
    return await this._httpService.doPostRequestAsync<any, any>(this._apiServer, this._controllerName, 'UpsertUser', updateUserRequest);
  }

  public announceActiveUser(user: User){
    this.activeUserSource.next(user);
  }

  public announceCorporationChange(corporationId:number){
    this.selectedCorporationChangeSource.next(corporationId);
  }

  public announceCorporationExport(isExport: boolean=false){
    this.corporationExportSource.next(isExport);
  }

  public checkEmailExist(email: string): Observable<ResponseStatus> {
    const params = new HttpParams().set('email', email);
    return  this._httpService.doGetRequestWithParams<any>(this._apiServer,this._controllerName, 'CheckEmailExists',params);
  }
}
